<template>
  <div>
    <b-modal v-model="visible" size="xl" :title="$t('Clientes Vinculados')" hide-footer @hidden="onHidden">
      <div v-if="affiliate">
        <b-row class="mb-3 align-items-center">
          <b-col md="6">
            <h5 class="mb-0">{{ affiliate.name }} ({{ affiliate.token }})</h5>
          </b-col>
          <b-col md="6" class="text-right d-flex justify-content-end gap-2">
            <button
              class="btn btn-primary btn-sm me-2"
              @click="openBulkTransferModal"
              :disabled="selectedIds.length === 0"
            >
              {{ $t('Transferir Selecionados') }} ({{ selectedIds.length }})
            </button>
            <button class="btn btn-primary btn-sm" @click="showTransferForm = !showTransferForm">
              {{ showTransferForm ? $t('Cancelar') : $t('Vincular Manualmente') }}
            </button>
          </b-col>
        </b-row>

        <div v-if="showTransferForm" class="card mb-3 bg-light">
          <div class="card-body">
            <h6>{{ $t('Vincular Cliente Manualmente') }}</h6>
            <b-row>
              <b-col md="9">
                <label>{{ $t('Identificador do Cliente (id_usuario_sp)') }}</label>
                <b-form-input v-model="form.identifier" type="text" :placeholder="$t('Ex: 123456')" />
              </b-col>
              <b-col md="3" class="d-flex align-items-end">
                <button class="btn btn-primary w-100" @click="transferCustomer" :disabled="!form.identifier">
                  {{ $t('Vincular') }}
                </button>
              </b-col>
            </b-row>
          </div>
        </div>

        <div v-if="isLoading" class="text-center py-4">
          <b-spinner variant="primary"></b-spinner>
        </div>
        <div v-else>
          <table class="table table-striped table-bordered mt-2 align-middle">
            <thead>
              <tr>
                <th style="width: 40px" class="text-center">
                  <b-form-checkbox
                    :checked="allSelected"
                    :indeterminate="isIndeterminate"
                    @change="toggleSelectAll"
                  />
                </th>
                <th class="text-center" style="width: 120px">{{ $t('Options') }}</th>
                <th style="width: 70px">ID</th>
                <th>id_usuario_sp</th>
                <th class="text-center">{{ $t('Transações') }}</th>
                <th class="text-center">{{ $t('Tipo') }}</th>
                <th class="text-center">{{ $t('Brand') }}</th>
                <th>{{ $t('Data') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="customer in linkedCustomers" :key="customer.id">
                <td class="text-center">
                  <b-form-checkbox
                    :checked="selectedIds.includes(customer.id)"
                    @change="toggleSelectCustomer(customer.id)"
                  />
                </td>
                <td class="text-center">
                  <button
                    v-if="customer.is_transferred"
                    class="btn btn-danger btn-sm"
                    @click="removeTransfer(customer.transfer_id)"
                  >
                    {{ $t('Desvincular') }}
                  </button>
                  <button v-else class="btn btn-primary btn-sm" @click="openSingleTransfer(customer)">
                    {{ $t('Transferir') }}
                  </button>
                </td>
                <td>{{ customer.id }}</td>
                <td>
                  <strong>{{ customer.identifier }}</strong>
                </td>
                <td class="text-center">
                  <span class="badge badge-soft-primary fs-6">{{ customer.transactions_count || 0 }}</span>
                </td>
                <td class="text-center">
                  <span v-if="customer.is_transferred" class="badge badge-soft-info">
                    {{ $t('Manual') }}
                  </span>
                  <span v-else class="badge badge-soft-secondary">
                    {{ $t('Orgânico') }}
                  </span>
                </td>
                <td class="text-center">{{ customer.id_brand || '-' }}</td>
                <td>{{ formatDate(customer.join_date || customer.join_moment || customer.transfer_date) }}</td>
              </tr>
              <tr v-if="linkedCustomers.length === 0">
                <td colspan="8" class="text-center py-3">{{ $t('Nenhum cliente vinculado encontrado.') }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </b-modal>

    <!-- Sub-modal para transferência em massa -->
    <b-modal
      v-model="showBulkModal"
      :title="$t('Transferir Clientes')"
      hide-footer
      centered
      @hidden="targetAffiliateId = null"
    >
      <div v-if="affiliate">
        <p>
          {{ $t('Você selecionou') }} <strong>{{ selectedIds.length }}</strong>
          {{ $t('cliente(s) deste afiliado para transferir.') }}
        </p>

        <div class="mb-3">
          <label class="form-label font-weight-bold">{{ $t('Afiliado Destino') }}</label>
          <affiliateSelector
            :idBrand="affiliateBrandId"
            :modelValue="targetAffiliateId"
            @onChange="targetAffiliateId = $event"
          />
        </div>

        <div class="d-flex justify-content-end gap-2 mt-4">
          <button class="btn btn-secondary me-2" @click="showBulkModal = false">
            {{ $t('Cancelar') }}
          </button>
          <button
            class="btn btn-primary"
            @click="confirmBulkTransfer"
            :disabled="!targetAffiliateId || targetAffiliateId === affiliate.id || isSubmittingBulk"
          >
            <b-spinner small v-if="isSubmittingBulk" class="me-1"></b-spinner>
            {{ $t('Confirmar Transferência') }}
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import affiliateSelector from '@/components/selectors/affiliateSelector';

export default {
  components: {
    affiliateSelector
  },
  data() {
    return {
      visible: false,
      affiliate: null,
      linkedCustomers: [],
      selectedIds: [],
      isLoading: false,
      showTransferForm: false,
      showBulkModal: false,
      targetAffiliateId: null,
      isSubmittingBulk: false,
      form: {
        identifier: ''
      }
    };
  },
  computed: {
    affiliateBrandId() {
      if (Array.isArray(this.affiliate?.allowed_brands) && this.affiliate.allowed_brands.length > 0) {
        return Number(this.affiliate.allowed_brands[0]);
      }
      return null;
    },
    allSelected() {
      return this.linkedCustomers.length > 0 && this.selectedIds.length === this.linkedCustomers.length;
    },
    isIndeterminate() {
      return this.selectedIds.length > 0 && this.selectedIds.length < this.linkedCustomers.length;
    }
  },
  methods: {
    show(affiliate) {
      this.affiliate = affiliate;
      this.visible = true;
      this.showTransferForm = false;
      this.showBulkModal = false;
      this.selectedIds = [];
      this.form.identifier = '';
      this.targetAffiliateId = null;
      this.loadCustomers();
    },
    onHidden() {
      this.affiliate = null;
      this.linkedCustomers = [];
      this.selectedIds = [];
    },
    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleString();
    },
    toggleSelectAll(checked) {
      if (checked) {
        this.selectedIds = this.linkedCustomers.map((c) => c.id);
      } else {
        this.selectedIds = [];
      }
    },
    toggleSelectCustomer(id) {
      const index = this.selectedIds.indexOf(id);
      if (index > -1) {
        this.selectedIds.splice(index, 1);
      } else {
        this.selectedIds.push(id);
      }
    },
    openSingleTransfer(customer) {
      this.selectedIds = [customer.id];
      this.targetAffiliateId = null;
      this.showBulkModal = true;
    },
    openBulkTransferModal() {
      if (this.selectedIds.length === 0) return;
      this.targetAffiliateId = null;
      this.showBulkModal = true;
    },
    loadCustomers() {
      if (!this.affiliate) return;
      this.isLoading = true;
      const params = {};
      if (this.affiliateBrandId) {
        params.idBrand = this.affiliateBrandId;
      }
      this.$api
        .get(`api/affiliate/${this.affiliate.id}/customerAffiliateTransfer`, params)
        .then((res) => {
          this.linkedCustomers = res.data || [];
          this.selectedIds = [];
        })
        .catch((err) => {
          this.$customAlert({
            type: 'error',
            msg: err?.response?.data?.message || this.$t('Error loading customers')
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    transferCustomer() {
      if (!this.form.identifier) return;
      this.$store.commit('SET_LOADING', true);

      const brandId = this.affiliateBrandId;

      const payload = { identifier: this.form.identifier };
      if (brandId) {
        payload.brandId = brandId;
      }

      this.$api
        .post(`api/affiliate/${this.affiliate.id}/customerAffiliateTransfer`, payload)
        .then(() => {
          this.$customAlert({ type: 'success', msg: this.$t('Cliente vinculado com sucesso!') });
          this.form.identifier = '';
          this.showTransferForm = false;
          this.loadCustomers();
        })
        .catch((err) => {
          this.$customAlert({
            type: 'error',
            msg: err?.response?.data?.message || this.$t('Erro ao vincular cliente')
          });
        })
        .finally(() => {
          this.$store.commit('SET_LOADING', false);
        });
    },
    confirmBulkTransfer() {
      if (!this.targetAffiliateId || this.selectedIds.length === 0) return;

      this.isSubmittingBulk = true;
      this.$api
        .post('api/customerAffiliateTransfer/bulk', {
          targetAffiliateId: this.targetAffiliateId,
          customerIds: this.selectedIds
        })
        .then(() => {
          this.$customAlert({
            type: 'success',
            msg: this.$t('Transferência realizada com sucesso!')
          });
          this.showBulkModal = false;
          this.selectedIds = [];
          this.loadCustomers();
        })
        .catch((err) => {
          this.$customAlert({
            type: 'error',
            msg: err?.response?.data?.message || this.$t('Erro ao realizar transferência')
          });
        })
        .finally(() => {
          this.isSubmittingBulk = false;
        });
    },
    removeTransfer(customerAffiliateTransferId) {
      if (
        confirm(
          this.$t('Deseja realmente desvincular este cliente? A regra de comissão voltará ao afiliado original.')
        )
      ) {
        this.$store.commit('SET_LOADING', true);
        this.$api
          .destroy(`api/customerAffiliateTransfer/${customerAffiliateTransferId}`)
          .then(() => {
            this.$customAlert({ type: 'success', msg: this.$t('Cliente desvinculado com sucesso!') });
            this.loadCustomers();
          })
          .catch((err) => {
            this.$customAlert({
              type: 'error',
              msg: err?.response?.data?.message || this.$t('Erro ao desvincular cliente')
            });
          })
          .finally(() => {
            this.$store.commit('SET_LOADING', false);
          });
      }
    }
  }
};
</script>
